import React, { useCallback, useState } from "react";
import {
  Grid,
  Button,
  Divider,
  Box,
  Stack,
  Icon,
  Typography,
  ButtonGroup,
  ClickAwayListener,
  Grow,
  Paper,
  Popper,
  MenuItem,
  MenuList,
  CircularProgress,
} from "@mui/material";
import { Formik } from "formik";
import * as Yup from "yup";
import PODetailHeader from "../../molecules/PurchaseOrder/PODetailHeader";
import { useLocation } from "react-router-dom";
import DashboardLayout from "../../layouts/DashboardLayout";
import { getPurchaseOrderDetail } from "../../api/purchaseOrders";
import { useSnack } from "../../services/SnakbarProvider";
import { handleError } from "../../services/errorHandler";
import { useCompany } from "../../contexts/CompanyContext";
import PODetailMaterialModalCrud from "../../organisms/PurchaseOrders/PODetailMaterialModalCrud";
import LGModal from "../../molecules/LGModal";
import ProductCrud from "../../organisms/Products/ProductCrud";
import PurchaseOrderForm from "../../organisms/PurchaseOrders/PurchaseOrderForm";
import {
  createHandleApprove,
  PDF_OPTIONS,
  usePODetailCatalogs,
  usePODetailPdfMenu,
} from "./poDetailShared";

const PODetailMaterialScreen = () => {
  const location = useLocation();
  const { openSnack } = useSnack();
  const POdata = location.state || {};
  const id = POdata.id;
  const isMaterialPO = POdata?.purchaseOrderType?.code === "POTYPE_MATERIAL";
  const { selectedCompany } = useCompany();
  const [headerModalOpen, setHeaderModalOpen] = useState(false);
  const [showProductTable, setShowProductTable] = useState(true);

  const { expenseAccounts, units, catalogsLoading } = usePODetailCatalogs(
    POdata,
    selectedCompany?.id,
  );
  const {
    pdfMenuOpen,
    selectedPdfIndex,
    pdfAnchorRef,
    handlePdfClick,
    handlePdfMenuItemClick,
    handlePdfToggle,
    handlePdfClose,
  } = usePODetailPdfMenu(id, POdata, openSnack);

  const handleApprove = createHandleApprove(id, openSnack);

  const initialValues = {
    purchaseOrder: { ...POdata },
    items: POdata.items ?? [],
  };

  const refreshPurchaseOrder = useCallback(async (setFieldValue) => {
    try {
      const response = await getPurchaseOrderDetail(id);
      await setFieldValue("purchaseOrder", response.data);
      await setFieldValue("items", response.data.items ?? []);
    } catch (e) {
      openSnack(handleError(e), "error");
    }
  }, [id, openSnack]);

  if (!selectedCompany) {
    return (
      <DashboardLayout>
        <Box p={4}>
          <Typography>Seleccione una compañía primero</Typography>
        </Box>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Box p={2}>
        {catalogsLoading ? (
          <CircularProgress />
        ) : (
          <Formik
            initialValues={initialValues}
            validationSchema={Yup.object().shape({})}
            onSubmit={() => {}}
          >
            {({ values, setFieldValue }) => {
              const handleItemsChanged = () => refreshPurchaseOrder(setFieldValue);

              return (
                <Grid container spacing={2}>
                  <Grid
                    item
                    xs={12}
                    sx={{
                      position: "sticky",
                      top: 50,
                      backgroundColor: "background.default",
                      zIndex: 1000,
                      paddingTop: 2,
                      paddingBottom: 2,
                    }}
                  >
                    <PODetailHeader />
                    <Divider sx={{ marginTop: 2 }} />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      variant="outlined"
                      onClick={() => setHeaderModalOpen(true)}
                      startIcon={<Icon>edit</Icon>}
                    >
                      Modificar ODC
                    </Button>
                  </Grid>

                  <Grid item xs={12}>
                    <LGModal
                      open={headerModalOpen}
                      handleClose={() => setHeaderModalOpen(false)}
                      header="Editar orden de compra"
                    >
                      <PurchaseOrderForm id={id} companyId={selectedCompany?.id} />
                    </LGModal>
                  </Grid>

                  <Grid item xs={12}>
                    <PODetailMaterialModalCrud
                      purchaseOrderId={id}
                      companyId={selectedCompany?.id}
                      isImport={values.purchaseOrder?.isImport || false}
                      expenseAccounts={expenseAccounts}
                      units={units}
                      onItemsChanged={handleItemsChanged}
                    />
                  </Grid>

                  <Grid item xs={12} spacing={2}>
                    <Stack direction="row" spacing={2}>
                      <Button variant="contained" onClick={handleApprove} color="success">
                        Aprobar
                      </Button>
                      <Box>
                        <ButtonGroup variant="contained" color="primary" ref={pdfAnchorRef}>
                          <Button onClick={handlePdfClick}>
                            <Icon>{PDF_OPTIONS[selectedPdfIndex].icon}</Icon>
                            {PDF_OPTIONS[selectedPdfIndex].label}
                          </Button>
                          <Button
                            size="small"
                            aria-controls={pdfMenuOpen ? "pdf-split-menu" : undefined}
                            aria-expanded={pdfMenuOpen ? "true" : undefined}
                            aria-haspopup="menu"
                            onClick={handlePdfToggle}
                          >
                            <Icon>arrow_drop_down</Icon>
                          </Button>
                        </ButtonGroup>
                        <Popper
                          sx={{ zIndex: 1300 }}
                          open={pdfMenuOpen}
                          anchorEl={pdfAnchorRef.current}
                          role={undefined}
                          transition
                          disablePortal
                        >
                          {({ TransitionProps, placement }) => (
                            <Grow
                              {...TransitionProps}
                              style={{
                                transformOrigin:
                                  placement === "bottom" ? "center top" : "center bottom",
                              }}
                            >
                              <Paper>
                                <ClickAwayListener onClickAway={handlePdfClose}>
                                  <MenuList id="pdf-split-menu" autoFocusItem>
                                    {PDF_OPTIONS.map((option, index) => (
                                      <MenuItem
                                        key={option.label}
                                        selected={index === selectedPdfIndex}
                                        onClick={(event) => handlePdfMenuItemClick(event, index)}
                                      >
                                        {option.label}
                                      </MenuItem>
                                    ))}
                                  </MenuList>
                                </ClickAwayListener>
                              </Paper>
                            </Grow>
                          )}
                        </Popper>
                      </Box>
                      <Button
                        variant="outlined"
                        onClick={() => setShowProductTable((prev) => !prev)}
                        startIcon={<Icon>{showProductTable ? "visibility_off" : "visibility"}</Icon>}
                      >
                        {showProductTable ? "Ocultar productos" : "Mostrar productos"}
                      </Button>
                    </Stack>
                  </Grid>
                </Grid>
              );
            }}
          </Formik>
        )}
      </Box>

      {isMaterialPO && showProductTable && (
        <Box mt={4} px={2}>
          <Divider sx={{ my: 3 }} />
          <Typography variant="h4">Productos</Typography>
          <ProductCrud companyId={selectedCompany?.id} />
        </Box>
      )}
    </DashboardLayout>
  );
};

export default React.memo(PODetailMaterialScreen);
